import { BraintreeTokenizePayload } from '@paypal/react-paypal-js/dist/types/types/braintree/commonsTypes';
import {
	CheckoutElementsProvider,
	ExpressCheckoutElement,
	PaymentElement,
	useCheckoutElements,
} from '@stripe/react-stripe-js/checkout';
import type {
	StripeCheckoutElementsSdkOptions,
	StripeCheckoutExpressCheckoutElementOptions,
	StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useIntl } from 'react-intl';

import { Notice, SecondaryButton, Text } from '@calm-web/design-system';

import Loader from '@/components/Loader';
import { fetchActiveSubscriptionWithPolling } from '@/components/purchase/PaymentElementsForm/fetchActiveSubscriptionWithPolling';
import { isSubscriptionResponse } from '@/components/purchase/PaymentElementsForm/types';
import PaymentElementsFormSubmitButton from '@/components/purchase/PaymentElementsFormSubmitButton';
import {
	desktopStripeAppearanceOptions,
	stripeAppearanceOptions,
} from '@/components/purchase/StripeElementsProvider/utils';
import usePurchaseBodyArgs from '@/components/Sisu/hooks/usePurchaseBodyArgs';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';
import { useAnalytics } from '@/hooks/analytics';
import { ProductInfo } from '@/hooks/analytics/types';
import { useApi } from '@/hooks/api';
import { usePaymentFormValues } from '@/hooks/purchase/usePaymentFormValues';
import { usePaypalClientToken } from '@/hooks/purchase/usePaypalClientToken';
import { useUserState } from '@/hooks/store';
import useIsDesktop from '@/hooks/ui/useIsDesktop';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { FAMILY_PLAN_YEARLY_PLAN_ID } from '@/server/initState/getFamilyPrices';
import { Subscription } from '@/store/user/types';
import { calmLogger } from '@/utils/calmLogger';
import { getClassicStripe } from '@/utils/getStripe';
import { extractErrorMessage, toError } from '@/utils/payment/extractErrorMessage';
import { JSONObject } from '@/utils/types';

import { NoticeWrapper, Wrapper } from '../PaymentElementsForm/styles';
import useSubmitPaymentRequest from '../PaymentElementsForm/useSubmitPaymentRequest';
import PaypalButton from '../PaypalButton';
import SecureTransaction from '../SecureTransaction';
import { SUPPORT_URL } from './constants';
import messages from './messages';
import { clearCheckoutSuccessPayload, persistCheckoutSuccessPayload } from './persistSuccessPayload';
import { isOneTimeFulfillmentFailed, pollCheckoutSessionStatus } from './pollCheckoutSessionStatus';
import {
	ExpressCheckoutWrapper,
	FormContainer,
	GROUP_GAP,
	PaymentElementWrapper,
	PaypalWrapper,
	UpdatingOverlay,
} from './styles';
import useCheckoutSessionPurchaseSuccess from './useCheckoutSessionPurchaseSuccess';
import useCreateCheckoutSession, {
	buildCheckoutSessionSignature,
	type CheckoutSessionCreateResult,
} from './useCreateCheckoutSession';
import useIsUnmountedRef from './useIsUnmountedRef';
import useLatestRef from './useLatestRef';

export type CheckoutSessionsFormProps = {
	purchaseArgs?: Record<string, unknown>;
	productInfo?: ProductInfo;
	successArgs?: Record<string, unknown>;
	ctaCopy?: string | ((plan: string | undefined) => string | undefined);
	afterPurchaseSuccess?: () => void;
	// The caller's own endpoint resolver (same shape PaymentElementsFormRefresh passes to the
	// legacy PaymentElementsForm branch). Card/wallet confirms never use this — Checkout Sessions
	// creation and confirm() always target the session's own endpoint — but the PayPal
	// (Braintree) path below does: it's a classic POST outside the session, so it must resolve to
	// whatever endpoint this specific purchase actually needs (e.g. `familyplan/create/braintree`
	// for a family PayPal purchase), not always the default `subscription`. Optional (defaulting
	// to `'subscription'` where it's consumed) so every existing caller that never had a reason to
	// pass this — every purchase this form has ever served until family — is unaffected.
	endpoint?: string | ((args: Record<string, unknown>) => string);
	// Hide the Braintree PayPal button (parity with PaymentElementsForm's `hidePaypal`) — some
	// callers/flows suppress PayPal.
	hidePaypal?: boolean;
	// Analytics attribution parity with PaymentElementsForm. The prefixed funnel events (Success /
	// Pending / Error / Cancelled) use `analyticsPrefix` (default 'Subscribe : Purchase : Form'), and
	// `analyticsEventProps` is merged into every funnel event EXCEPT `Subscribe : Payment Type :
	// Selected` — legacy fires that one without it, so CS matches. `Subscription : Upsell : Clicked`
	// keeps its fixed (un-prefixed) name like legacy but DOES carry `analyticsEventProps`. The
	// invariant test in index.test.tsx enforces this across the funnel so a future holdout fails CI.
	analyticsPrefix?: string;
	analyticsEventProps?: Record<string, string>;
	// Notifies the caller when a session creation this component is solely responsible for
	// surfacing fails: either the FIRST (initial, pre-mount) create for a mount, or — because a
	// mid-session toggle INTO a family or Lifetime purchase goes through the outer form's own
	// background create effect instead, whose failure otherwise has nowhere else to go — a
	// background create for a later plan/coupon/promo change on one of those plans. Only when
	// provided, the initial case also suppresses the built-in error Notice + "Try again" UI so the
	// caller can swap in a fallback form instead of ever showing it (the background-create case has
	// no such UI to suppress — see `backgroundCreateStatus`). Omit to keep the error/overlay UI with
	// no callback.
	onInitialCreateFailed?: (err: unknown) => void;
};

// The Stripe wallet buttons (Apple Pay / GPay / Link inside the ExpressCheckoutElement) render at
// 44px. Match the treatment's own custom buttons — the Braintree PayPal button and the submit
// button — to that height so the whole stack lines up (before, the submit was the design-system
// default 56px and stood a bit taller than the wallets). PayPal is also ROUNDED (`pill`) to match
// the wallets' 36px appearance radius (the Braintree SDK exposes only `rect`/`pill`; on a 44px
// button a 36px radius reads as a full pill). CS-only — passed via props, so the control flow's
// buttons are untouched.
const WALLET_BUTTON_HEIGHT = 44;
const PAYPAL_BUTTON_SHAPE = 'pill' as const;

// Maps the PaymentElement's selected method (Stripe's snake_case type) to the `payment_type`
// analytics value. Cards keep the long-standing `credit_card` value so existing charts hold,
// and every other method (`sepa_debit`, `klarna`, ...) reports Stripe's own type.
const toPaymentType = (paymentMethodType: string): string =>
	paymentMethodType === 'card' ? 'credit_card' : paymentMethodType;

// The session enables `automatic_tax`, so the Payment Element must collect a billing address for
// the tax calculation. `fields.billingDetails.address: 'auto'` lets Stripe render the minimum
// required address fields per payment method inline in the Payment Element (Stripe's recommended
// minimal tax-address collection). The session collects no shipping address,
// so tax reads the billing address — this replaces the separate `BillingAddressElement` + the old
// billing-as-shipping reuse. Module-level (referentially stable) so the element isn't reconfigured
// each render. https://docs.stripe.com/payments/payment-element/control-fields
const PAYMENT_ELEMENT_OPTIONS = { fields: { billingDetails: { address: 'auto' as const } } };

// Pin the express wallet buttons so Apple Pay and Google Pay stay ahead of Link. Without this the
// Express Checkout Element orders wallets dynamically per user, which can float the Link button to
// the front. Stripe ignores any listed method that doesn't render and appends any rendered method
// not listed, so the short list is safe. The Custom Checkout options type lists every field, so the
// ones we don't set are passed as undefined (their value types allow it) to keep Stripe's defaults.
// Module-level (referentially stable) so the element isn't reconfigured each render.
// https://docs.stripe.com/js/elements_object/create_express_checkout_element
const EXPRESS_CHECKOUT_OPTIONS: StripeCheckoutExpressCheckoutElementOptions = {
	buttonHeight: undefined,
	buttonTheme: undefined,
	buttonType: undefined,
	layout: undefined,
	paymentMethodOrder: ['apple_pay', 'google_pay', 'link'],
	paymentMethods: undefined,
};

type CheckoutSessionsFormInnerProps = Pick<
	CheckoutSessionsFormProps,
	| 'productInfo'
	| 'successArgs'
	| 'ctaCopy'
	| 'afterPurchaseSuccess'
	| 'purchaseArgs'
	| 'hidePaypal'
	| 'endpoint'
	| 'analyticsPrefix'
	| 'analyticsEventProps'
> & {
	// The created session; `confirm()` awaits it for the sessionId (the redirect-persist
	// key). Awaiting the already-resolved promise avoids a post-paint ref-timing race.
	sessionPromise: Promise<CheckoutSessionCreateResult>;
	onRetry: () => void;
	// True while `useCreateCheckoutSession` has a create in flight or coalesced behind one.
	// Disables submit/express-checkout so the buyer can never confirm a session that's about to
	// be superseded by a selection change still resolving. Cosmetic-only backstop — the
	// functional guard is the signature/committedSignature ref comparison below.
	isCreating: boolean;
	// Owned by the outer, unkeyed `CheckoutSessionsForm` (survives this component's own
	// key-driven remounts) and mutated synchronously on every render there, so `confirm()`
	// can compare them with no stale-closure window. `signatureRef` is the live selection;
	// `committedSignatureRef` is the selection THIS mounted instance's session actually
	// belongs to. They diverge exactly while a newer selection's session is still being
	// created in the background — the window a stale confirm must be blocked in.
	signatureRef: RefObject<string>;
	committedSignatureRef: RefObject<string>;
	// Also owned by the outer form — drives the loading/error overlay rendered here (scoped to
	// just the payment-options block below, not the whole form) while a background create for a
	// newer selection is in flight or has failed.
	isUpdating: boolean;
	backgroundCreateStatus: 'pending' | 'failed';
	onRetryUpdate: () => void;
};

const CheckoutSessionsFormInner: React.FC<CheckoutSessionsFormInnerProps> = ({
	productInfo,
	successArgs,
	ctaCopy,
	afterPurchaseSuccess,
	purchaseArgs,
	hidePaypal,
	endpoint = 'subscription',
	analyticsPrefix = 'Subscribe : Purchase : Form',
	analyticsEventProps,
	sessionPromise,
	onRetry,
	isCreating,
	signatureRef,
	committedSignatureRef,
	isUpdating,
	backgroundCreateStatus,
	onRetryUpdate,
}) => {
	const checkoutState = useCheckoutElements();
	const apiRequest = useApi();
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const { paymentFormCurrency } = usePaymentFormValues();
	// Funnel event fired on a payment-method button CLICK (card / wallet / PayPal), matching the
	// legacy PaymentElementsForm's `Subscription : Upsell : Clicked` (fixed name; not prefixed) so
	// treatment lines up with control in Amplitude.
	const logUpsellClicked = useCallback(
		(paymentType: string) => {
			logEvent({
				eventName: 'Subscription : Upsell : Clicked',
				eventProps: { payment_type: paymentType, is_stripe_next: true, ...analyticsEventProps },
			});
		},
		[logEvent, analyticsEventProps],
	);
	// Localized error fallbacks (resolved once per render; stable per locale). Buyers should never
	// see hardcoded English on the no-message path — Stripe's own messages are already localized.
	const providerErrorFallback = formatMessage(messages.providerError);
	const confirmErrorFallback = formatMessage(messages.confirmError);
	const updatingPaymentFormCopy = formatMessage(messages.updatingPaymentForm);
	const updatePaymentFormFailedCopy = formatMessage(messages.updatePaymentFormFailed);
	// Shared between the error early-return branch below and the main-render overlay further
	// down, so the two copies of each can't drift out of sync on a future edit.
	const updatingLoader = <Loader announcement={updatingPaymentFormCopy} />;
	const updateRetryButton = (
		<SecondaryButton onPress={onRetryUpdate} data-testid="checkout-sessions-update-retry">
			{formatMessage(messages.tryAgain)}
		</SecondaryButton>
	);
	const routerPush = useRouterPush();
	// Safe everywhere: the context has a no-op default, so off-modal hosts (e.g. the
	// /signup-flow page, Sisu) get `isModalOpen === false` + a no-op `setIsModalOpen`.
	const { isModalOpen, setIsModalOpen } = useSimplifiedSignupModalContext();
	const { buildPayload, fireSuccess, firePending } = useCheckoutSessionPurchaseSuccess({
		productInfo,
		successArgs,
		analyticsPrefix,
		analyticsEventProps,
	});
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	// The engaged payment method ('credit_card' on card focus, the wallet type on express confirm,
	// 'paypal' via PaypalButton). Drives `Payment Type : Selected` and the PayPal loader scope.
	const [selectedPaymentType, setSelectedPaymentType] = useState<string | null>(null);
	// The PaymentElement's currently selected method, read at confirm time. A ref because it only
	// feeds analytics and must not re-render or re-create the confirm callback.
	const paymentElementMethodTypeRef = useRef('card');
	// `Subscribe : Payment Type : Selected` — fires when the buyer engages a method. Matches legacy
	// PaymentElementsForm exactly (fixed name + props).
	useEffect(() => {
		if (selectedPaymentType) {
			logEvent({
				eventName: 'Subscribe : Payment Type : Selected',
				eventProps: { payment_type: selectedPaymentType, is_stripe_next: true },
			});
		}
	}, [selectedPaymentType, logEvent]);
	// Brief "finalizing your subscription…" state for the success-pending path (confirm
	// succeeded but fulfillment lagged past the poll window). Locks the form so the user
	// can't re-submit / re-create while we auto-advance to /app (the webhook fulfills).
	const [isFinalizing, setIsFinalizing] = useState(false);
	// Terminal: a one-time purchase was charged but its grant failed. Locks the form, since a
	// second confirm would charge again, and points the buyer to support.
	const [isFulfillmentFailed, setIsFulfillmentFailed] = useState(false);
	// PaymentElement completeness (all required fields valid, including the inline billing address
	// collected via `fields.billingDetails.address: 'auto'`). Gates the card submit button so a
	// buyer can't click Pay into a guaranteed validation error.
	const [isPaymentComplete, setIsPaymentComplete] = useState(false);
	// The Checkout Elements provider reaching `success` only means the clientSecret resolved —
	// the PaymentElement's own iframe still loads asynchronously after that. `onLoaderStart`
	// (Stripe's internal loader mounting) or `onReady` (fully interactive) are the earliest
	// points something is actually visible in that slot; until then we show our own Loader
	// instead of a silent blank region.
	const [isPaymentElementVisible, setIsPaymentElementVisible] = useState(false);
	const revealPaymentElement = useCallback(() => setIsPaymentElementVisible(true), []);
	// Synchronous in-flight guard. `isLoading`/`isFinalizing` are React state read through
	// this callback's closure, so a second wallet tap dispatched before the first render
	// commits would see the stale `false` and fire a second confirm → double charge. A ref
	// flips synchronously, closing that sub-render-tick window (the state guard + the
	// disabled express element cover the steady state).
	const confirmInFlightRef = useRef(false);
	// Aborts the post-confirm `subscriptions/active` poll if the form unmounts mid-poll (e.g. the
	// buyer navigates away): the helper then stops issuing requests instead of running out its
	// ~60s budget in the background. Only ever aborted on unmount, so a bailed poll has nothing
	// left to render.
	const pollAbortRef = useRef<AbortController | null>(null);
	useEffect(() => () => pollAbortRef.current?.abort(), []);

	const confirm = useCallback(
		async (expressCheckoutConfirmEvent?: StripeExpressCheckoutElementConfirmEvent) => {
			if (checkoutState.type !== 'success') {
				return;
			}
			// This mounted instance's Elements/session belong to `committedSignatureRef`, not
			// necessarily whatever the buyer has selected NOW (`signatureRef`) — a plan/coupon/promo
			// change fires a background create for the new selection while this stale instance stays
			// mounted and interactive (see the outer form). A confirm reaching here while the two
			// differ would charge for a selection the buyer has already moved past — including via an
			// Apple/Google Pay sheet opened before the change and confirmed after — so this must be a
			// synchronous ref read, not React state (no stale-closure window). A fresh session is
			// already on its way, so this never sets `errorMessage` / shows an app-level error —
			// `paymentFailed()` still dismisses an open wallet sheet from the buyer's perspective
			// (that's its purpose), it just isn't treated as a payment failure on our side.
			if (signatureRef.current !== committedSignatureRef.current) {
				expressCheckoutConfirmEvent?.paymentFailed();
				return;
			}
			// Re-entrancy guard: a confirm is already in flight (submitting) or the session is
			// finalizing (post-confirm poll). Without this, a second wallet tap during the
			// up-to-60s poll fires a second confirm. The ref catches a synchronous double-tap
			// the state guard would miss. Dismiss the express sheet so it doesn't linger, then bail.
			if (confirmInFlightRef.current || isLoading || isFinalizing || isFulfillmentFailed) {
				expressCheckoutConfirmEvent?.paymentFailed();
				return;
			}
			confirmInFlightRef.current = true;
			setIsLoading(true);
			setErrorMessage(null);

			try {
				// Express wallets report their own type. The embedded PaymentElement reports the
				// method the buyer selected in it (card, SEPA, Klarna, ...).
				const selectedPaymentType = expressCheckoutConfirmEvent
					? expressCheckoutConfirmEvent.expressPaymentType
					: toPaymentType(paymentElementMethodTypeRef.current);
				// `Upsell : Clicked` for the PaymentElement submit (parity with legacy onStripeSubmit).
				// The express/wallet path fires it earlier from the element's onClick (matching
				// legacy); firing here too would double-count the wallet click.
				if (!expressCheckoutConfirmEvent) {
					logUpsellClicked(selectedPaymentType);
				}
				// Built once (stable amp_order_id) and used for both the on-page event
				// and the persisted redirect-fallback replay.
				const payload = buildPayload(selectedPaymentType);
				// Read the sessionId from the (already-resolved by now) create promise rather than
				// a post-paint ref, so the redirect-persist key is reliably available even on a
				// fast Pay click. The defensive `.catch` keeps a failed create from throwing here —
				// confirm already early-returns when the provider isn't `success`.
				const sessionId = (await sessionPromise.catch(() => null))?.session.sessionId ?? null;

				// --- Up to and including confirm(): a failure here IS a real payment error. ---
				try {
					// The Payment Element collects the billing address inline
					// (`fields.billingDetails.address: 'auto'`) and `confirm()` submits it for
					// `automatic_tax`. The session collects no shipping address,
					// so there is no `updateShippingAddress` step on either the card or express path.
					if (sessionId) {
						persistCheckoutSuccessPayload(sessionId, payload);
					}

					// No `returnUrl` here: the backend already set the session's `return_url`
					// at create time for the `elements` surface (Stripe rejects providing it
					// twice). The session's own `return_url` drives the redirect (3DS) flow.
					const result = await checkoutState.checkout.confirm({
						redirect: 'if_required',
						expressCheckoutConfirmEvent,
					});

					if (result.type === 'error') {
						if (sessionId) {
							clearCheckoutSuccessPayload(sessionId);
						}
						calmLogger.error(
							'CheckoutSessionsForm: confirm failed',
							{ message: result.error.message },
							result.error instanceof Error
								? result.error
								: new Error(result.error.message || 'confirm error'),
						);
						// Never surface an empty error bar — fall back to a clear message.
						setErrorMessage(result.error.message || confirmErrorFallback);
						// `${prefix} : Error` funnel event (parity with legacy onPurchaseError).
						logEvent({
							eventName: `${analyticsPrefix} : Error`,
							eventProps: {
								currency: paymentFormCurrency,
								payment_type: selectedPaymentType,
								is_stripe_next: true,
								...(result.error.message ? { error: result.error.message } : {}),
								...analyticsEventProps,
							},
						});
						// Dismiss the express wallet sheet on failure so it doesn't linger open.
						expressCheckoutConfirmEvent?.paymentFailed();
						setIsLoading(false);
						return;
					}
				} catch (err) {
					// confirm() threw → real failure. Surface it (with a
					// message extracted even from non-Error throws) instead of swallowing.
					if (sessionId) {
						clearCheckoutSuccessPayload(sessionId);
					}
					const message = extractErrorMessage(err);
					calmLogger.error('CheckoutSessionsForm: confirm threw', { message }, toError(err));
					// `||` (not `??`) so an empty-string message also falls back to clear copy —
					// matches the sibling error branches and "never surface an empty error bar".
					setErrorMessage(message || confirmErrorFallback);
					// `${prefix} : Error` funnel event (parity with legacy onPurchaseError).
					logEvent({
						eventName: `${analyticsPrefix} : Error`,
						eventProps: {
							currency: paymentFormCurrency,
							payment_type: selectedPaymentType,
							is_stripe_next: true,
							...(message ? { error: message } : {}),
							...analyticsEventProps,
						},
					});
					// Dismiss the express wallet sheet on failure so it doesn't linger open.
					expressCheckoutConfirmEvent?.paymentFailed();
					setIsLoading(false);
					return;
				}

				// --- Past here, confirm() succeeded → the payment/setup is DONE. ---
				// The CONFIRMED purchase event fires ONLY on server-confirmed fulfillment (match
				// control's discipline + Stripe's model: the fulfillment webhook is the source of
				// truth; the client readback is a "fulfill-when-present" optimization). Poll
				// `subscriptions/active` (it 200s once the webhook lands) and branch three ways:
				//   • resolves WITH a subscription → fulfillment confirmed → fire the confirmed
				//     event (logPurchase + Success) and advance via `setUser` → Handoff.
				//   • THREW (404 across the budget / transient error) → paid but fulfillment not yet
				//     confirmed (Stripe "processing") → advance the UX (they paid) but fire ONLY the
				//     pending funnel event — never the confirmed/value event.
				//   • resolves with a NON-subscription body → unexpected/malformed → surface an
				//     error; never fire success on a non-subscription response (matches control).
				if (sessionId) {
					clearCheckoutSuccessPayload(sessionId);
				}

				// Contract (app-api): `subscriptions/active` 404s until the fulfillment webhook
				// lands, then 200s with the subscription. So a legitimately-lagging sub THROWS
				// (404 across the budget) → processing; a 200 that isn't a subscription is a
				// genuinely unexpected/malformed response (not mere lag).
				let subscription: Subscription | undefined;
				// Distinguishes a genuinely malformed body (not subscription-shaped → error) from a
				// subscription-shaped-but-`valid:false` body (benign provisioning lag → warn, like a 404).
				let sawNonSubscriptionBody = false;
				// Scope this poll to an AbortController held in a ref so the unmount effect can abort it.
				// (A prior attempt's controller is already settled — the poll lives inside this callback's
				// try/finally with the in-flight guard — so aborting it here is a harmless no-op.)
				pollAbortRef.current?.abort();
				const pollController = new AbortController();
				pollAbortRef.current = pollController;
				try {
					const { data } = await fetchActiveSubscriptionWithPolling(apiRequest, {
						pollIntervalMs: 3000,
						maxAttempts: 20,
						// `subscriptions/active` selects by `expires`, not validity, so right after a
						// resubscribe it can 200 with a stale CANCELED sub (`valid:false`) while the new
						// sub is still `incomplete`. Keep polling past a non-valid sub until the new one
						// activates — otherwise we'd treat the canceled sub as a confirmed purchase and
						// `setUser` a `valid:false` sub, which the Handoff gate rejects → payment loop.
						requireValid: true,
						signal: pollController.signal,
					});
					// Gate on validity, not just shape: a `valid:false` body is NOT confirmed fulfillment.
					if (isSubscriptionResponse(data)) {
						if (data.valid) {
							subscription = data;
						} else {
							// Subscription-shaped but `valid:false` after the whole budget — a webhook-lag /
							// resubscribe window we polled past without the new sub activating. Benign (handled
							// as processing below), but warn for observability (parity with the 404-across-budget
							// warn) — the real failed-state signal lands elsewhere; this is the
							// web-side stopgap.
							calmLogger.warn(
								'CheckoutSessionsForm: active-subscription poll exhausted with a valid:false sub (fulfillment lag)',
								{},
							);
						}
					} else {
						sawNonSubscriptionBody = true;
					}
				} catch (err) {
					// An unmount aborted the poll (navigation) AFTER confirm() already succeeded — the buyer
					// PAID. Stop polling, but do NOT drop the conversion: unlike the /checkout/return page
					// (which replays via the persisted payload + processed marker on a remount), the on-page
					// path has no client-side replay, so a dropped event silently undercounts the funnel.
					// Fire the PENDING funnel event fire-and-forget (never the confirmed/enriched event —
					// fulfillment is unconfirmed) and bail. Every poll-abort here is post-confirm: the poll
					// is created only after confirm() succeeds.
					if (pollController.signal.aborted) {
						firePending({ selectedPaymentType }).catch(() => {});
						return;
					}
					// 404 across the poll budget (or a transient error) → fulfillment not yet
					// confirmed. Payment is confirmed, so this is processing, not a failure.
					calmLogger.warn(
						'CheckoutSessionsForm: fulfillment not yet confirmed (payment confirmed, processing)',
						{ message: extractErrorMessage(err) ?? null },
					);
				}

				// The form unmounted between the poll resolving and here (post-confirm) → fire the PENDING
				// event fire-and-forget (see the abort note in the catch above) and bail.
				if (pollController.signal.aborted) {
					// Fire-and-forget (component is unmounting) — swallow any analytics rejection.
					firePending({ selectedPaymentType }).catch(() => {});
					return;
				}

				if (subscription) {
					// CONFIRMED fulfillment → confirmed purchase event + advance to Handoff.
					try {
						await fireSuccess(payload, subscription);
					} catch (err) {
						// Analytics/state failure must not surface as a payment error post-confirm.
						calmLogger.error('CheckoutSessionsForm: post-confirm success handling failed', {}, toError(err));
					}
					afterPurchaseSuccess?.();
					// `setUser` routed SimplifiedSignup to Handoff (unmounting this form); clear the
					// spinner as a safety net in case navigation doesn't occur.
					setIsLoading(false);
				} else {
					// A one-time grant that already failed never produces a subscription, so the poll
					// above cannot tell it apart from lag. Read the session once to tell them apart.
					let oneTimeGrantFailed = false;
					if (sessionId) {
						try {
							const sessionStatus = await pollCheckoutSessionStatus(apiRequest, sessionId, {
								maxAttempts: 1,
								signal: pollController.signal,
							});
							oneTimeGrantFailed = isOneTimeFulfillmentFailed(sessionStatus);
						} catch (err) {
							if (!pollController.signal.aborted) {
								calmLogger.warn('CheckoutSessionsForm: session-status read after confirm failed', {
									message: extractErrorMessage(err) ?? null,
								});
							}
						}
					}
					// The read does not cancel an in-flight request, so a response can land after the
					// form unmounted. Bail like the subscription poll above so it never overrides navigation.
					if (pollController.signal.aborted) {
						firePending({ selectedPaymentType }).catch(() => {});
						return;
					}
					if (oneTimeGrantFailed) {
						const failureMessage = 'CheckoutSessionsForm: one-time purchase charged but fulfillment failed';
						calmLogger.error(failureMessage, { sessionId }, new Error(failureMessage));
						setIsFulfillmentFailed(true);
						setIsLoading(false);
						return;
					}
					// NOT YET CONFIRMED — payment succeeded but fulfillment isn't server-confirmed.
					// All sub-cases are treated the SAME UX-wise (payment is done, so never show a
					// misleading "payment failed" error or re-enable the form → double charge):
					//   • the poll threw (404 across budget) = Stripe "processing" (benign, warned in catch);
					//   • a subscription-shaped `valid:false` sub (stale/lagging) = benign provisioning lag
					//     (warned above) — the resubscribe-after-cancel case;
					//   • a genuinely NON-subscription body = unexpected/malformed → logged at error level.
					// Either way, fire ONLY the pending funnel event (never logPurchase / the confirmed
					// event), lock the form, and reach a terminal state (the webhook fulfills out-of-band).
					if (sawNonSubscriptionBody) {
						calmLogger.error(
							'CheckoutSessionsForm: active-subscription poll returned a non-subscription response',
							{},
							new Error('active-subscription poll returned a non-subscription response'),
						);
					}
					try {
						await firePending({ selectedPaymentType });
					} catch (err) {
						calmLogger.error('CheckoutSessionsForm: pending-event firing failed', {}, toError(err));
					}
					afterPurchaseSuccess?.();
					if (isModalOpen) {
						// In the modal (e.g. resubscribe from Manage Subscription), a `routerPush('/app')`
						// no-ops under the persistent overlay and a permanent `isFinalizing` lock would trap
						// the buyer. Enter a terminal "processing" state with an explicit exit
						// (`onProcessingDone` → dismiss the modal + navigate). `isFinalizing` replaces the
						// submit button (no duplicate confirm/create) until they acknowledge.
						setIsFinalizing(true);
						setIsLoading(false);
					} else {
						// On a page host (e.g. /signup-flow), navigation unmounts the form. Deliberately do
						// NOT clear `isLoading` here — keep the submit button disabled (isLoading gates it)
						// through the async `routerPush` gap so a second tap can't fire a duplicate
						// confirm/create before the form unmounts.
						// TODO: redirect/3DS buyers land on /app rather than the in-flow Handoff
						// app-download screen; app-download parity for this on-page processing exit is a
						// tracked follow-up.
						routerPush('/app');
					}
				}
			} finally {
				// Synchronous reset of an intentionally-mutable ref (not React state) — the
				// race the rule warns about doesn't apply.
				// eslint-disable-next-line require-atomic-updates
				confirmInFlightRef.current = false;
			}
		},
		[
			checkoutState,
			buildPayload,
			fireSuccess,
			firePending,
			apiRequest,
			afterPurchaseSuccess,
			sessionPromise,
			confirmErrorFallback,
			isModalOpen,
			routerPush,
			isLoading,
			isFinalizing,
			isFulfillmentFailed,
			logEvent,
			logUpsellClicked,
			analyticsPrefix,
			analyticsEventProps,
			paymentFormCurrency,
			signatureRef,
			committedSignatureRef,
		],
	);

	const onSubmit = useCallback(
		async (event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			await confirm();
		},
		[confirm],
	);

	// Exit from the terminal "processing" state. Dismiss the SimplifiedSignup modal
	// (no-op off-modal) AND navigate — so the buyer is never trapped behind an open modal that a
	// bare `routerPush` can't dismiss, regardless of whether this form is in the modal or a page.
	const onProcessingDone = useCallback(() => {
		setIsModalOpen(false);
		routerPush('/app');
	}, [setIsModalOpen, routerPush]);

	const onExpressConfirm = useCallback(
		async (event: StripeExpressCheckoutElementConfirmEvent) => {
			// Custom Checkout's `ExpressCheckoutElement` exposes NO `onClick` (the checkout variant's
			// type omits it), so — unlike legacy, which fires these on the wallet CLICK — the wallet
			// method and the `Payment Type : Selected` / `Upsell : Clicked` signals are only
			// observable at confirm time. This undercounts wallet sessions the buyer opens but
			// dismisses vs. control; see the PR note. (Cancelled is still captured via `onCancel`.)
			setSelectedPaymentType(event.expressPaymentType);
			logUpsellClicked(event.expressPaymentType);
			await confirm(event);
		},
		[confirm, logUpsellClicked],
	);

	// Wallet sheet dismissed → `${prefix} : Cancelled` (express/wallet-only — there is no card-path
	// cancel event in either flow). `payment_type` is hardcoded `'express'`, NOT read from
	// `selectedPaymentType`: Custom Checkout's `ExpressCheckoutElement` has no `onClick`, so the
	// wallet type is only known at confirm — at dismiss time `selectedPaymentType` is stale
	// (null/'credit_card'/'paypal', whatever was last touched). The Stripe cancel event
	// (`{ elementType: 'expressCheckout' }`) doesn't carry `expressPaymentType` either, so the true
	// wallet type is unobservable on dismiss. `'express'` is honest (don't fabricate a wallet type);
	// DS must not slice Cancelled by wallet type (totals reconcile; the wallet split is unobservable).
	const onExpressCancel = useCallback(() => {
		logEvent({
			eventName: `${analyticsPrefix} : Cancelled`,
			eventProps: { payment_type: 'express', ...analyticsEventProps },
		});
	}, [logEvent, analyticsPrefix, analyticsEventProps]);

	// --- PayPal (Braintree) — parity with the control PaymentElementsForm ----------
	// PayPal is NOT a Stripe Checkout Sessions payment method: the Braintree PayPal SDK
	// tokenizes it and it's submitted through the classic `/subscription` POST (reusing
	// the control's `useSubmitPaymentRequest`), independent of `checkout.confirm()`. On
	// success we route via the same `fireSuccess` path the card/wallet flow uses. The
	// button, its position (below the wallet row), and the `isSupported` gating mirror
	// the control. Reached only for flows enrolled in `www-checkout-sessions`, same as
	// the rest of this form.
	const {
		clientToken: paypalClientToken,
		isSupported: isPaypalSupported,
		isLoading: isPaypalClientTokenFetching,
	} = usePaypalClientToken();
	// PaypalButton sets this to 'paypal' (in its createBillingAgreement) when the PayPal flow
	// starts; we read it to scope the PayPal button's loading state to PayPal activity only (so a
	// card-`Pay` click doesn't flip the PayPal button into its loading rectangle). useSubmitPaymentRequest
	// also reports completion via setSuccessfullySubmitted, which the CS form doesn't consume.
	const [, setSuccessfullySubmitted] = useState(false);

	const onPaypalPurchaseSuccess = useCallback(
		async (subscription: Subscription) => {
			// Reuse the card/wallet success path: setUser(valid sub) routes SimplifiedSignup
			// to Handoff, plus the purchase analytics from the (paypal) payload.
			// If the backend returned a stale `valid:false` sub, fireSuccess downgrades the
			// analytics to the Pending shape (no confirmed-conversion / Revenue event) and skips setUser.
			// Either way the buyer must still advance — matching the on-page card path above — so wrap the
			// analytics in try/catch (an analytics/state throw must not surface as a payment error
			// post-confirm) and run afterPurchaseSuccess regardless; the fulfillment webhook remains the
			// server-side source of truth.
			try {
				await fireSuccess(buildPayload('paypal'), subscription);
			} catch (err) {
				calmLogger.error('CheckoutSessionsForm: post-paypal success handling failed', {}, toError(err));
			}
			afterPurchaseSuccess?.();
		},
		[fireSuccess, buildPayload, afterPurchaseSuccess],
	);

	const onPaypalNoop = useCallback(async () => {
		// The SimplifiedSignup PayPal flow only creates subscriptions; the one-time
		// purchase branch isn't reachable here.
	}, []);

	const onPaypalError = useCallback(
		(message: string, eventProps?: JSONObject, errorCode?: string | JSONObject) => {
			// `${prefix} : Error` (parity with legacy onPurchaseError — field-for-field). The classic
			// submitter passes formatted error props + the calmCode, which flow through `eventProps`
			// / `errorCode` here. Card/wallet errors fire it from the confirm-failure sites.
			logEvent({
				eventName: `${analyticsPrefix} : Error`,
				eventProps: {
					currency: paymentFormCurrency,
					payment_type: 'paypal',
					is_stripe_next: true,
					...(errorCode ? { error: errorCode } : {}),
					...eventProps,
					...analyticsEventProps,
				},
			});
			setErrorMessage(message || confirmErrorFallback);
			setIsLoading(false);
		},
		[confirmErrorFallback, logEvent, analyticsPrefix, analyticsEventProps, paymentFormCurrency],
	);

	const onPaypalRequiresAction = useCallback(async () => {
		// PayPal vault billing agreements don't go through Stripe SCA, so a
		// requires_action here is unexpected — surface a clean error rather than hang.
		calmLogger.error(
			'CheckoutSessionsForm: unexpected requires_action on the PayPal path',
			{},
			new Error('Unexpected requires_action on the PayPal path'),
		);
		setErrorMessage(confirmErrorFallback);
		setIsLoading(false);
	}, [confirmErrorFallback]);

	const submitPaypalRequest = useSubmitPaymentRequest({
		// The caller's own resolver — same one PaymentElementsFormRefresh hands to the legacy
		// PaymentElementsForm branch. Usually resolves to the plain `subscription` endpoint (a
		// literal `'subscription'` here was equivalent as long as this form was only ever reached
		// via that path), but a family purchase's resolver is payment-method-dependent
		// (`familyplan/create/braintree` for paypal) — a hardcoded literal would post a family
		// PayPal purchase to the wrong endpoint.
		endpoint,
		// Attribution parity with the legacy PaymentElementsForm: prefix/props flow into the classic
		// submitter (which fires `${prefix} : Posted` etc.). (`pageName` is NOT
		// threaded: `useSubmitClassicPaymentRequest` never reads it — it's a dead prop in legacy too.)
		analyticsPrefix,
		analyticsEventProps,
		selectedPaymentType: 'paypal',
		hasTrueMedSupport: false,
		// Thread the caller's purchaseArgs into the PayPal `/subscription` POST so it matches the
		// card/wallet session-create body (which already carries it via useCreateCheckoutSession).
		purchaseArgs,
		setIsLoading,
		setSuccessfullySubmitted,
		onPurchaseError: onPaypalError,
		onPurchaseSuccess: onPaypalPurchaseSuccess,
		oneTimePurchaseSuccess: onPaypalNoop,
		onPurchaseSuccessOverride: undefined,
		onRequiresAction: onPaypalRequiresAction,
	});

	const onPaypalSubmit = useCallback(
		async (paypalToken: BraintreeTokenizePayload) => {
			setErrorMessage(null);
			setIsLoading(true);
			// `Upsell : Clicked` for the PayPal button (parity with legacy onPaypalSubmit).
			logUpsellClicked('paypal');
			await submitPaypalRequest({ paypalToken, paymentMethod: 'paypal' }, { payment_type: 'paypal' });
		},
		[submitPaypalRequest, logUpsellClicked],
	);

	// The provider reaches `error` when the session can't be created/loaded (e.g. a
	// backend 400/503 while the `clientSecret` promise resolves). Log it so the
	// failure isn't invisible in logs (it previously showed only as an empty red bar).
	const checkoutErrorMessage = checkoutState.type === 'error' ? checkoutState.error.message : undefined;
	useEffect(() => {
		if (checkoutState.type === 'error') {
			calmLogger.error(
				'CheckoutSessionsForm: checkout provider error',
				{ message: checkoutErrorMessage ?? null },
				new Error(checkoutErrorMessage || 'Unknown checkout provider error'),
			);
		}
	}, [checkoutState.type, checkoutErrorMessage]);

	if (checkoutState.type === 'loading') {
		// Shown on initial mount and while the session is recreated on a plan change.
		return <Loader />;
	}

	if (checkoutState.type === 'error') {
		return (
			<NoticeWrapper>
				<Notice isSuccess={false} describedBy="checkout-sessions-form">
					{/* Always the friendly fallback — never the provider's raw message, which can
					    be an internal string (e.g. the thrown "did not return a client secret").
					    The raw `error.message` is logged in the effect above for diagnosis. */}
					{providerErrorFallback}
				</Notice>
				{/* `onRetry` targets THIS instance's own (committed) signature and would either
				    no-op or race a newer selection's in-flight/failed create — show that update's
				    own progress or working retry instead of a button that quietly does nothing. */}
				{isUpdating ? (
					backgroundCreateStatus === 'failed' ? (
						updateRetryButton
					) : (
						updatingLoader
					)
				) : (
					<SecondaryButton onPress={onRetry} data-testid="checkout-sessions-retry">
						{formatMessage(messages.tryAgain)}
					</SecondaryButton>
				)}
			</NoticeWrapper>
		);
	}

	// Shared by the ExpressCheckoutWrapper's aria-busy and dim-style below — one evaluation
	// instead of two copies that could silently diverge on a future edit. `isUpdating` also
	// disables here: `isCreating` alone clears once a background create REJECTS (nothing left
	// queued), but the stale-signature guard still blocks a confirm until that failure's own
	// retry succeeds — without this, Pay/Express would look clickable right through it.
	const isExpressCheckoutBusy = isLoading || isFinalizing || isFulfillmentFailed || isCreating || isUpdating;

	return (
		<Wrapper onSubmit={onSubmit} id="checkout-sessions-form" data-testid="checkout-sessions-form">
			{/* Scoped to ONLY the payment-options block (wallets/PayPal/card) — not the submit
			    button, error notice, or SecureTransaction below — so the "Updating the payment
			    form" overlay never sits on top of unrelated content beneath it. */}
			<FormContainer>
				{/* Express wallets + PayPal + the card form read as one tight, evenly-spaced
				    group: the express + PayPal wrappers carry a uniform bottom gap
				    and there's no "Or" divider (the PaymentElement holds several methods, so an
				    "Or" would imply a false binary and fence PayPal off). */}
				{/* Disable wallet interaction while a confirm is in flight / finalizing / a create is
				    superseding this session (parity with the PayPal button's `isDisabled`): the
				    ExpressCheckoutElement has no disabled prop, so block pointer events + dim. The
				    confirm() re-entrancy guard is the functional backstop; this prevents the tap in
				    the first place. */}
				<ExpressCheckoutWrapper
					aria-busy={isExpressCheckoutBusy}
					style={isExpressCheckoutBusy ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
				>
					<ExpressCheckoutElement
						options={EXPRESS_CHECKOUT_OPTIONS}
						onConfirm={onExpressConfirm}
						onCancel={onExpressCancel}
					/>
				</ExpressCheckoutWrapper>
				{/* PayPal (Braintree) below the wallet row — a classic `/subscription` POST, not
				    part of the Stripe Checkout Session. */}
				{isPaypalSupported && !hidePaypal && (
					<PaypalWrapper>
						<PaypalButton
							setSelectedPaymentType={setSelectedPaymentType}
							onSubmit={onPaypalSubmit}
							onError={onPaypalError}
							clientToken={paypalClientToken}
							isLoading={isPaypalClientTokenFetching ?? (selectedPaymentType === 'paypal' && isLoading)}
							// Matches the wallet wrapper's dimming: PayPal's own `/subscription` POST reads
							// live plan state at submit time (not the mounted session), but it should still
							// dim in step with the rest of the form during a background create.
							isDisabled={isExpressCheckoutBusy}
							analyticsPrefix={analyticsPrefix}
							analyticsEventProps={analyticsEventProps}
							buttonShape={PAYPAL_BUTTON_SHAPE}
							buttonHeight={WALLET_BUTTON_HEIGHT}
						/>
					</PaypalWrapper>
				)}
				{/* `automatic_tax` is enabled server-side, so the Payment Element collects the billing
				    address inline via `fields.billingDetails.address: 'auto'` (the minimum required tax
				    fields per payment method) — no separate BillingAddressElement / shipping reuse. */}
				{/* Covers the gap between the provider reaching `success` and the element itself
				    painting anything: it stays hidden but mounted (so it can still fire
				    `onLoaderStart`/`onReady`), with our own Loader shown in its place. Also gated on
				    `!isUpdating` — this component doesn't remount on a live-only signature change, so
				    both this and the overlay's Loader could otherwise mount at once and
				    double-announce to screen readers. */}
				{!isPaymentElementVisible && !isUpdating && <Loader />}
				<PaymentElementWrapper $isVisible={isPaymentElementVisible}>
					<PaymentElement
						options={PAYMENT_ELEMENT_OPTIONS}
						onChange={event => {
							setIsPaymentComplete(event.complete);
							// Switching methods inside the element (e.g. the card tab to SEPA) doesn't
							// re-fire onFocus, so the change event is what reports the new method.
							if (event.value.type !== paymentElementMethodTypeRef.current) {
								paymentElementMethodTypeRef.current = event.value.type;
								setSelectedPaymentType(toPaymentType(event.value.type));
							}
						}}
						onFocus={() => setSelectedPaymentType(toPaymentType(paymentElementMethodTypeRef.current))}
						onLoaderStart={revealPaymentElement}
						onReady={revealPaymentElement}
					/>
				</PaymentElementWrapper>
				{isUpdating && (
					<UpdatingOverlay
						data-testid="checkout-sessions-updating"
						$interactive={backgroundCreateStatus === 'failed'}
					>
						{backgroundCreateStatus === 'failed' ? (
							<>
								<Notice isSuccess={false}>{updatePaymentFormFailedCopy}</Notice>
								{updateRetryButton}
							</>
						) : (
							// No visible label alongside the spinner — the announcement is the a11y
							// channel; a sighted buyer reads the spinner itself as "something is happening".
							updatingLoader
						)}
					</UpdatingOverlay>
				)}
			</FormContainer>
			{errorMessage && (
				<NoticeWrapper>
					<Notice isSuccess={false} describedBy="checkout-sessions-form">
						{errorMessage}
					</Notice>
				</NoticeWrapper>
			)}
			{isFulfillmentFailed ? (
				<>
					<Text el="p" data-testid="checkout-sessions-fulfillment-failed">
						{formatMessage(messages.fulfillmentFailed)}
					</Text>
					<SecondaryButton href={SUPPORT_URL}>{formatMessage(messages.contactSupport)}</SecondaryButton>
				</>
			) : isFinalizing ? (
				// Terminal "processing" state: payment succeeded but fulfillment isn't
				// server-confirmed yet (Stripe async / webhook lag). Reassure + give an explicit exit
				// (`onProcessingDone` dismisses the modal AND navigates) instead of a permanently
				// disabled submit button behind a modal that a `routerPush` can't dismiss.
				<>
					<Text el="p" data-testid="checkout-sessions-processing">
						{formatMessage(messages.processingPending)}
					</Text>
					<SecondaryButton onPress={onProcessingDone} data-testid="checkout-sessions-processing-done">
						{formatMessage(messages.processingDone)}
					</SecondaryButton>
				</>
			) : (
				// Disabled until the card path is actually submittable — a complete PaymentElement
				// (which now includes the inline billing address) — so a click can't land on a
				// guaranteed validation error. Also disabled while a create supersedes this session
				// (`isCreating`) or has already failed to (`isUpdating` — see `isExpressCheckoutBusy`'s
				// own comment), so confirm can never target a session that's about to be, or already
				// should have been, replaced. Sized to the Stripe wallet / PayPal buttons (44px) with
				// the group gap above it so the button stack reads as one consistent block.
				<PaymentElementsFormSubmitButton
					ctaCopy={ctaCopy}
					isDisabled={isLoading || isCreating || isUpdating || !isPaymentComplete}
					isLoading={isLoading}
					height={`${WALLET_BUTTON_HEIGHT}px`}
					marginTop={GROUP_GAP}
				/>
			)}
			<SecureTransaction />
		</Wrapper>
	);
};

type CheckoutSessionsProviderProps = CheckoutSessionsFormProps & {
	// Created in the un-keyed outer form (not here) so its dedupe cache survives this
	// provider's plan/retry remounts — see `CheckoutSessionsForm` below. (`purchaseArgs` is still
	// forwarded so the inner PayPal `/subscription` POST gets it, even though the create hook is
	// built in the outer form.)
	createCheckoutSession: ReturnType<typeof useCreateCheckoutSession>['createCheckoutSession'];
	onRetry: () => void;
	isCreating: boolean;
	signatureRef: RefObject<string>;
	committedSignatureRef: RefObject<string>;
	// Set only when this mount is a deferred-commit remount (the outer form already resolved a
	// session for the signature this instance is being keyed with) — reusing it here avoids a
	// second, redundant create for a selection that's already settled. Undefined on the very
	// first mount and on a "Try again" remount (the outer form deliberately doesn't seed those),
	// so this provider's own effect creates fresh in both of those cases, same as before.
	initialSessionPromise?: Promise<CheckoutSessionCreateResult> | null;
	isUpdating: boolean;
	backgroundCreateStatus: 'pending' | 'failed';
	onRetryUpdate: () => void;
	// Reports the sessionId this instance's OWN session resolves to, once it does — the outer
	// form uses this to tell "a fresh POST resolved with the SAME signature string as what's
	// already committed" (e.g. a revert that re-creates after an abandoned detour already
	// expired the original session) apart from "nothing actually changed," which `committedSignature`
	// alone can't distinguish.
	onSessionMounted: (sessionId: string) => void;
};

/**
 * The Stripe Checkout Sessions replacement for the manual-PaymentMethod
 * `PaymentElementsForm`, gated behind the `www-checkout-sessions` experiment
 * (enrolled at 100% treatment) plus the eligibility conjunction and
 * `CHECKOUT_SESSIONS_ELIGIBLE_FLOW_NAMES` allowlist in `PaymentElementsFormRefresh` —
 * this component mounts today for real users on the allowlisted flows.
 *
 * Primary path: `checkout.confirm({ redirect: 'if_required' })` stays on-page
 * for standard confirms and fires the purchase event in-form (full control
 * parity via `createSuccessEventProps`). Redirect-required confirms (3DS) fall
 * back to `/checkout/return`, which replays the persisted event and routes to /app.
 * Card, Apple/Google Pay (express), and PayPal (Braintree) are all wired.
 */
const CheckoutSessionsProvider: React.FC<CheckoutSessionsProviderProps> = ({
	createCheckoutSession,
	productInfo,
	successArgs,
	ctaCopy,
	afterPurchaseSuccess,
	purchaseArgs,
	hidePaypal,
	endpoint,
	analyticsPrefix,
	analyticsEventProps,
	onRetry,
	isCreating,
	signatureRef,
	committedSignatureRef,
	initialSessionPromise,
	isUpdating,
	backgroundCreateStatus,
	onRetryUpdate,
	onSessionMounted,
	onInitialCreateFailed,
}) => {
	const isDesktop = useIsDesktop();
	// Set only once the initial (non-seeded) create for THIS mount rejects and a caller passed
	// `onInitialCreateFailed` — see the effect and render gate below. Never set when the callback
	// is absent, so an uninstrumented caller's error+retry UI is completely unaffected.
	const [initialCreateFailed, setInitialCreateFailed] = useState(false);

	// Create the session in an EFFECT (not during render): the create POST and the dedupe-cache
	// mutation in `useCreateCheckoutSession` are side effects and belong in commit, per react.dev.
	// The hook's content-signature dedupe makes this idempotent — under StrictMode's
	// mount→cleanup→remount the second run reuses the SAME in-flight promise, so exactly ONE POST
	// fires. That is a correctness guarantee, not just an optimization: the api keeps one open
	// session per user, so a re-POST would expire the mounted session out from under the buyer. This
	// component is keyed `${committedSignature}-${retryAttempt}` by its parent, so it remounts on a
	// "Try again" or once a plan/coupon/promo change's background create resolves and commits (see
	// the outer form) — never on the live selection changing by itself.
	const [sessionPromise, setSessionPromise] = useState<Promise<CheckoutSessionCreateResult> | null>(
		() => initialSessionPromise ?? null,
	);
	// Gates the effect below to "create at most once per mount" — via a REF, not by adding
	// `sessionPromise` to the effect's own deps, which would make the `setSessionPromise` call
	// inside the effect retrigger its own cleanup/rerun on the very next render for no reason.
	// Starts `true` when this mount was seeded with an already-resolved session (the
	// deferred-commit case).
	const hasSessionRef = useRef(sessionPromise !== null);
	// `createCheckoutSession`'s identity churns on any live signature change even though this
	// provider only cares about `committedSignature` — gating the catch below on THIS (rather
	// than a per-effect-run `ignore` flag) means that unrelated churn can't silence its
	// backend-failure log.
	const isUnmountedRef = useIsUnmountedRef();
	useEffect(() => {
		// Seeded (deferred-commit) or already created once — either way, no second create for a
		// selection that's already settled or still the same one.
		if (hasSessionRef.current) {
			return;
		}
		hasSessionRef.current = true;
		const promise = createCheckoutSession();
		// Expose the effect-created session PROMISE to render (one intended Loader→form cascade): the
		// provider consumes it as `options.clientSecret`, and a create rejection surfaces through the
		// provider's own error state (the inner form's error branch). Holding the promise in state is
		// the prescribed shape here, so the "no synchronous setState in an effect" perf guard doesn't
		// apply — this is a deliberate one-time "resource created in an effect" hand-off.
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setSessionPromise(promise);
		// Surface a creation failure (a backend 400/503) — the provider only ever exposes a
		// normalized message, so this is the one place the raw error is visible. `apiRequest` rejects
		// with the axios AxiosResponse (`{ status, data, headers, … }`), not an `Error` — the backend
		// reason is at `err.data.error.message`, so use the shared `extractErrorMessage`/`toError`
		// helpers (which read that path) rather than `instanceof Error`, which would discard it.
		// Logging is intentionally NOT gated on this effect re-running for an unrelated reason
		// (see `isUnmountedRef`'s comment above) — only on the component having actually unmounted.
		promise.catch(err => {
			if (isUnmountedRef.current) {
				return;
			}
			calmLogger.error(
				'CheckoutSessionsForm: failed to create checkout session',
				{ message: extractErrorMessage(err) ?? null },
				toError(err),
			);
			// Opt-in only: an uninstrumented caller still falls through to the provider's own
			// `checkoutState.type === 'error'` branch below, unchanged.
			if (onInitialCreateFailed) {
				onInitialCreateFailed(err);
				setInitialCreateFailed(true);
			}
		});
	}, [createCheckoutSession, isUnmountedRef, onInitialCreateFailed]);

	// Reports which session this instance is actually using, once it resolves — regardless of
	// whether that promise was created above or handed in already-resolved (`initialSessionPromise`).
	// The outer form needs this to tell a genuinely new session apart from a same-signature no-op.
	useEffect(() => {
		if (!sessionPromise) {
			return undefined;
		}
		let ignore = false;
		sessionPromise
			.then(({ session }) => {
				if (!ignore) {
					onSessionMounted(session.sessionId);
				}
			})
			.catch(() => {
				// A creation failure has no session to report — the error branch below surfaces it.
			});
		return () => {
			ignore = true;
		};
	}, [sessionPromise, onSessionMounted]);

	// `CheckoutElementsProvider` takes the `clientSecret` directly (a Promise is allowed) rather than a
	// `fetchClientSecret` callback. A rejection propagates to the provider, which surfaces
	// `useCheckoutElements().type === 'error'` (handled + logged by the inner form's error branch).
	const clientSecret = useMemo(
		() =>
			sessionPromise?.then(({ session }) => {
				if (!session.clientSecret) {
					// `surface: 'elements'` always returns a clientSecret; a null here means
					// an unexpected backend response (e.g. a 'hosted' session).
					throw new Error('Checkout Session did not return a client secret');
				}
				return session.clientSecret;
			}) ?? null,
		[sessionPromise],
	);

	// Calm-themed Elements chrome (parity with the legacy StripeElementsProvider / Sisu
	// PaymentScreen): without this the embedded Elements render with Stripe's default theme. Use the
	// 481px desktop breakpoint (`useIsDesktop`) so it matches the rest of the payment screen —
	// `useIsMobile` is a 1024px breakpoint and would diverge from 482–1023px.
	const appearance = isDesktop ? desktopStripeAppearanceOptions : stripeAppearanceOptions;

	// `adaptivePricing.allowed` is now supported by the SDK (resolves the earlier
	// "Adaptive Pricing is not allowed on the client" error). NOTE: for Custom Checkout
	// (`initCheckout`), `appearance` lives under `elementsOptions` — NOT as a top-level option;
	// passing it at the top level throws `IntegrationError: options.appearance is not an accepted
	// parameter` and the provider never mounts (blank form). The `StripeCheckoutElementsSdkOptions`
	// annotation makes that a compile error rather than a runtime-only failure.
	const options: StripeCheckoutElementsSdkOptions | null = useMemo(
		() =>
			clientSecret
				? { clientSecret, adaptivePricing: { allowed: true }, elementsOptions: { appearance } }
				: null,
		[clientSecret, appearance],
	);

	// The create runs in an effect, so the promise/clientSecret aren't ready on the first render —
	// show the same Loader the inner form uses until they are, then mount the provider.
	if (!sessionPromise || !options) {
		return <Loader />;
	}

	// The initial create for this mount already rejected and a caller opted in via
	// `onInitialCreateFailed` (already notified above) — never mount `CheckoutElementsProvider`,
	// whose Stripe-driven `checkoutState.type === 'error'` would otherwise show the built-in error
	// Notice + "Try again". A Loader is fine here: the caller is expected to unmount this whole
	// tree and swap in its own fallback.
	if (initialCreateFailed) {
		return <Loader />;
	}

	return (
		<CheckoutElementsProvider stripe={getClassicStripe()} options={options}>
			<CheckoutSessionsFormInner
				sessionPromise={sessionPromise}
				productInfo={productInfo}
				successArgs={successArgs}
				ctaCopy={ctaCopy}
				afterPurchaseSuccess={afterPurchaseSuccess}
				purchaseArgs={purchaseArgs}
				hidePaypal={hidePaypal}
				endpoint={endpoint}
				analyticsPrefix={analyticsPrefix}
				analyticsEventProps={analyticsEventProps}
				onRetry={onRetry}
				isCreating={isCreating}
				signatureRef={signatureRef}
				committedSignatureRef={committedSignatureRef}
				isUpdating={isUpdating}
				backgroundCreateStatus={backgroundCreateStatus}
				onRetryUpdate={onRetryUpdate}
			/>
		</CheckoutElementsProvider>
	);
};

const CheckoutSessionsForm: React.FC<CheckoutSessionsFormProps> = ({
	purchaseArgs,
	onInitialCreateFailed,
	...props
}) => {
	const routerPush = useRouterPush();
	// Same content-signature body `useCreateCheckoutSession` derives `plan` from — computed once
	// here (not re-called per use) so both the family-purchase check below and `signature` further
	// down read the SAME resolved plan.
	const purchaseBodyArgs = usePurchaseBodyArgs({ purchaseArgs });
	// A family purchase's primary audience is an existing valid individual/premium subscriber
	// upgrading to family (see familyPlan/SignupScreens/index.tsx's determineNextScreenAfterAccount)
	// — mirrors the identical narrowing in useCreateCheckoutSession.ts; see that comment for the
	// full reasoning. Every other purchase's target type is whatever the buyer already has (there's
	// only one subscription per user), so the narrowing is a no-op for them.
	const isFamilyPurchase = purchaseBodyArgs.plan === FAMILY_PLAN_YEARLY_PLAN_ID;
	// Plans whose caller falls back to the legacy form when the api refuses their session.
	const hasLegacyFallback = isFamilyPurchase || purchaseBodyArgs.plan === 'lifetime';
	const user = useUserState();
	// A user who is ALREADY subscribed when they first land on this payment form arrived at a
	// stale checkout (e.g. a back-button or deep link after they'd already purchased). There's no
	// session to show and a create would just be rejected with `already_subscribed`, so send them
	// to /app — the same destination the signup-flow server guard redirects paid users to (and the
	// behavior a handoff-page refresh already gives). Mirrors existing UX, not new.
	//
	// CRITICAL: snapshot the status AT MOUNT (`useState` initial value), never the live value.
	// This is what scopes the redirect to the fresh-arrival edge and keeps it from hijacking the
	// success flow: a successful on-page purchase flips `subscription.valid` true LATER (setUser),
	// and that buyer must continue to the Handoff app-download screen, NOT be bounced to /app.
	// They were NOT subscribed at mount, so `arrivedSubscribed` stays false for them. (Equivalent
	// to the hook guard's `hasActiveSubscription && !cacheRef.current?.promise`: subscribed before
	// any session existed here = a fresh arrival; subscribed after = a completed purchase.)
	const subscribedNow = !!(
		user?.subscription?.valid &&
		(!isFamilyPurchase || user.subscription.type === 'family')
	);
	const [arrivedSubscribed] = useState(subscribedNow);
	// Snapshotted at mount, same as `arrivedSubscribed` — a STABLE mount-time fact, deliberately
	// NOT re-derived from the live `isFamilyPurchase`/`subscribedNow` below. A family purchase's
	// buyer can legitimately already own a valid, non-family subscription (the narrowing above
	// exists exactly so THEIR mount isn't treated as a fresh arrival) — but `PlanSkuToggle` can
	// then flip the live plan away from family mid-session, at which point `isFamilyPurchase`
	// flips false and the SAME narrowing would retroactively un-exempt them, making the guard
	// below wrongly think a purchase just completed for a plan that was never even attempted.
	// Snapshotting `wasValidAtMount` keeps that from happening: it only ever reflects whether the
	// buyer already had ANY valid subscription before this mount ever attempted anything,
	// independent of which plan is live now.
	const [wasValidAtMount] = useState(!!user?.subscription?.valid);
	// LIVE — unlike the two snapshots above, this must track a purchase completing WHILE this
	// component stays mounted. Deliberately keyed on the mount-to-now `.valid` TRANSITION
	// (false → true), not the live narrowed `subscribedNow`: a confirmed purchase for a buyer who
	// had NO subscription at mount flips `subscription.valid` true, which flips the derived
	// `freeTrial` false and therefore the live `signature` — but there is no new session to chase:
	// `useCreateCheckoutSession` itself already refuses to POST once subscribed
	// (`already_subscribed`), returning either the stale cached result or a promise that never
	// resolves at all. Without this guard, that post-success signature churn would trip the
	// background pre-fetch effect below and show the "Updating the payment form" overlay over a
	// form the buyer just successfully paid on — indefinitely, since nothing here would ever move
	// `committedSignature` to unstick it. A buyer who already had a valid (non-family) subscription
	// at mount never satisfies this transition, regardless of how they toggle plans afterward — so,
	// unlike the bug this replaces, toggling AWAY from family for such a buyer correctly does NOT
	// retroactively bail the background create for their new (non-family) selection.
	const hasCompletedPurchaseThisMount = !wasValidAtMount && !!user?.subscription?.valid;

	// The clientSecret is bound to a session created for one specific plan/coupon/promotion/
	// freeTrial. Key the provider subtree by the SAME content signature that
	// `useCreateCheckoutSession` dedupes on (not just plan): the hook POSTs a new session
	// whenever any of those changes, so the key must change in lockstep — otherwise a
	// coupon/promotion/freeTrial change would create a fresh session without remounting, and
	// the mounted Elements would confirm the stale one (I9). Re-mounting resets the
	// PaymentElement (entered card clears) — inherent to Checkout Sessions; these fields are
	// stable per session, so it's one remount per genuine change, not churn.
	const signature = buildCheckoutSessionSignature(purchaseBodyArgs);
	// The signature the MOUNTED provider's session actually belongs to. Deliberately NOT bumped
	// in lockstep with `signature` — see the pre-fetch effect below. Only once a background
	// create for the new selection resolves does this move (to whatever signature the resolved
	// payload actually corresponds to, not necessarily `signature` at fire time — coalescing can
	// hand back a later selection's result), which is what changes the provider's key and swaps
	// the visible form. Bumped by the "Try again" affordance; folded into the provider key so a
	// retry remounts `CheckoutSessionsProvider`, re-running session creation (these
	// failures can be transient — a backend 400/503).
	const [committedSignature, setCommittedSignature] = useState(signature);
	const [retryAttempt, setRetryAttempt] = useState(0);

	// Kept current every render so `confirm()` — several components down, past the provider's
	// own remount boundary — can compare "what the buyer has selected now" against "what this
	// mounted session was created for" with no stale-closure window. The ref OBJECTS live here,
	// above the keyed subtree, so they survive the provider/inner form's key-driven remounts
	// untouched; only `.current` needs to be read fresh at confirm-time.
	const signatureRef = useLatestRef(signature);
	const committedSignatureRef = useLatestRef(committedSignature);

	// Created here, in the un-keyed outer form, so its content-signature dedupe cache
	// survives the keyed provider's remounts: a landing whose `plan` key flaps during
	// hydration (or a "Try again") remounts `CheckoutSessionsProvider`, but the same
	// `{plan,coupon,promotion,freeTrial}` signature returns the in-flight/last promise
	// → ONE create per distinct plan, not one POST per remount (which tripped the
	// backend 429). A genuine plan toggle changes the signature → exactly one new
	// create; a failed create clears the cache so the retry re-creates.
	const { createCheckoutSession, evict, isCreating } = useCreateCheckoutSession({ purchaseArgs });

	// Seeds the NEXT provider mount with an already-resolved session when that mount is a
	// deferred-commit remount (the effect below just resolved a create for `committedSignature`)
	// — the provider's own effect then reuses it instead of firing a second, redundant create for
	// a selection that's already settled. Reset to null on every "Try again" (which doesn't touch
	// `committedSignature`) so that remount always creates fresh, and never carries a prior,
	// possibly stale, resolved session forward.
	const [seedSessionPromise, setSeedSessionPromise] = useState<Promise<CheckoutSessionCreateResult> | null>(
		null,
	);

	// 'pending' while a background create for a newer selection is in flight; 'failed' once it
	// rejects. Drives the overlay's error state — without this, a failed background create left
	// `committedSignature` diverged from `signature` forever (the effect below only re-fires on a
	// DEPS change, and nothing else changes them), which stuck the "Updating…" overlay up
	// permanently while `isCreating` cleared underneath it — re-enabling a submit button whose
	// click would then silently no-op against the stale-signature guard forever, with zero
	// feedback to the buyer.
	const [backgroundCreateStatus, setBackgroundCreateStatus] = useState<'pending' | 'failed'>('pending');
	// Bumped by the overlay's own "Try again" action on a FAILED background create — distinct
	// from `retryAttempt` (which forces an immediate full provider remount for the currently
	// mounted signature) — so a buyer stuck on a failed update can force this effect to fire
	// again for the same (still-uncommitted) signature.
	const [updateRetryAttempt, setUpdateRetryAttempt] = useState(0);

	// What the effect below last actually called `createCheckoutSession()` for — NOT
	// `committedSignature`. A revert to `committedSignature` while a DETOUR is still queued behind
	// an in-flight create needs one more call to cancel that detour (the hook's own
	// `cacheRef.current?.signature === signature` branch); gating on `signature === committedSignature`
	// would skip that call and leave the detour to fire later, expiring the still-mounted session.
	const lastCalledSignatureRef = useRef(signature);
	const lastUpdateRetryAttemptRef = useRef(updateRetryAttempt);
	// Monotonically incrementing marker for "the most recent call this effect has issued" — NOT
	// the signature it was for. `useCreateCheckoutSession`'s own "settling back on this selection
	// cancels a queued detour" fast path can hand the SAME promise object back to TWO separate
	// effect runs (e.g.: fire for B, queue C behind it, revert to B before B settles — the revert
	// hits that fast path and gets B's own still-pending promise back again). Both runs would then
	// have the SAME signature at settle time, so comparing signatures alone lets both pass a
	// staleness check and both act on one real settle. Comparing sequence numbers instead means
	// only whichever run most recently incremented this ref is ever allowed to commit or log.
	const callSequenceRef = useRef(0);
	// `createCheckoutSession`'s identity can churn for reasons entirely unrelated to the checkout
	// signature (e.g. `useApi()`'s own identity depending on consent/analytics state) — gating
	// the settle-time checks below on THIS (rather than a per-effect-run `ignore` flag) means
	// that unrelated churn can't be mistaken for this effect's work having been superseded.
	const isUnmountedRef = useIsUnmountedRef();

	// The sessionId the MOUNTED provider is actually using, as it reports back via
	// `onSessionMounted`. Compared against a background create's resolved session below: a fresh
	// POST can resolve with the SAME signature string as what's already committed (e.g. a revert
	// that re-creates after an abandoned detour already expired the original session) — comparing
	// signatures alone would then wrongly treat that as a no-op and leave the valid new session
	// unmounted, orphaned in `seedSessionPromise`.
	const mountedSessionIdRef = useRef<string | null>(null);
	const onSessionMounted = useCallback((sessionId: string) => {
		mountedSessionIdRef.current = sessionId;
	}, []);
	// Bumped only when a background create resolves with a session that's actually different
	// from the one currently mounted (by `mountedSessionIdRef`), regardless of whether its
	// signature textually matches `committedSignature` — folded into the provider's key below so
	// it always remounts onto the freshest resolved session, not just on a signature change.
	const [sessionGeneration, setSessionGeneration] = useState(0);

	// The buyer changed plan/coupon/promo (`signature` moved since the last call this effect made):
	// fire a create for the new selection in the BACKGROUND, without touching `committedSignature`
	// yet — the currently mounted provider (old key, old session, old Elements) stays exactly as it
	// was, visible and usable, for the whole time this is in flight. Committing (and therefore
	// remounting the provider) only happens once the create actually resolves, and only to the
	// signature the RESOLVED payload corresponds to — not `signature` as captured when this effect
	// fired, since `createCheckoutSession` can coalesce this call behind one already in flight and
	// hand back a later selection's result by the time it settles.
	useEffect(() => {
		// Once a purchase has completed THIS mount, a signature change is not a real plan/coupon/
		// promo update to chase — it's the post-purchase `freeTrial` flip (see
		// `hasCompletedPurchaseThisMount`'s own comment above). Bail before ever calling
		// `createCheckoutSession()`, which would otherwise hit its own `already_subscribed` guard
		// and hand back something that can never commit.
		if (hasCompletedPurchaseThisMount) {
			return undefined;
		}
		const signatureChanged = signature !== lastCalledSignatureRef.current;
		const isManualRetry = updateRetryAttempt !== lastUpdateRetryAttemptRef.current;
		if (!signatureChanged && !isManualRetry) {
			return undefined;
		}
		// Captured now, compared against the REF's later value at settle time — not an `ignore`
		// flag flipped by this effect's own cleanup, which can run for reasons unrelated to the
		// signature (see `isUnmountedRef`'s comment), and not the signature itself (see
		// `callSequenceRef`'s comment — two different runs can share one promise and one signature).
		callSequenceRef.current += 1;
		const mySequence = callSequenceRef.current;
		// Captured now (this render's value, fixed for this effect run) — not read fresh at
		// settle time, since a later render could have toggled the plan again by then.
		const isThisCreateFallbackScoped = hasLegacyFallback;
		lastCalledSignatureRef.current = signature;
		lastUpdateRetryAttemptRef.current = updateRetryAttempt;
		// Reset on every attempt (including a manual retry of a prior failure) so the overlay
		// doesn't keep showing a stale error banner while a fresh attempt is already in flight.
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setBackgroundCreateStatus('pending');
		const promise = createCheckoutSession();
		promise
			.then(result => {
				if (isUnmountedRef.current || mySequence !== callSequenceRef.current) {
					return;
				}
				setCommittedSignature(result.signature);
				setSeedSessionPromise(promise);
				if (result.session.sessionId !== mountedSessionIdRef.current) {
					setSessionGeneration(generation => generation + 1);
				}
			})
			.catch(err => {
				if (isUnmountedRef.current || mySequence !== callSequenceRef.current) {
					return;
				}
				// Unlike the provider's own create failure (which surfaces through its mounted
				// `checkoutState.type === 'error'` branch), this create belongs to no mounted
				// provider — the stale one stays on `checkoutState.type === 'success'` throughout —
				// so this is the only place this failure is ever visible. Log it and flip the
				// overlay to its error state rather than leaving the buyer stuck with no feedback.
				calmLogger.error(
					'CheckoutSessionsForm: failed to create a checkout session for a plan/coupon/promo change',
					{ message: extractErrorMessage(err) ?? null },
					toError(err),
				);
				setBackgroundCreateStatus('failed');
				// A mid-session toggle INTO family or Lifetime (e.g. via PlanSkuToggle) reaches this
				// background path, not the provider's initial-mount effect — `onInitialCreateFailed`
				// otherwise never fires for it, leaving the buyer stuck on this permanently-failed
				// overlay instead of falling back to the legacy form like every other such refusal.
				// `isThisCreateFallbackScoped` is the value from THIS effect run's own closure (the
				// render that fired this exact create), not a later one — safe to read directly here
				// for the same reason `mySequence`/`lastCalledSignatureRef` are: a fresh closure per
				// effect run, fixed at fire time. Never fires for any other plan.
				if (onInitialCreateFailed && isThisCreateFallbackScoped) {
					onInitialCreateFailed(err);
				}
			});
	}, [
		signature,
		createCheckoutSession,
		updateRetryAttempt,
		hasCompletedPurchaseThisMount,
		isUnmountedRef,
		hasLegacyFallback,
		onInitialCreateFailed,
	]);

	const onRetryUpdate = useCallback(() => {
		setUpdateRetryAttempt(attempt => attempt + 1);
	}, []);

	// "Try again" on the MOUNTED provider's own error state (its initial create failed). Evicts
	// the dedupe cache FIRST (a session can resolve with a valid clientSecret yet fail Stripe
	// Elements init — that resolved-but-dead session would otherwise replay on the remount since
	// retryAttempt isn't in the signature), THEN clears any leftover seed (a retry must always
	// create fresh, never reuse a previously-resolved session) and bumps the provider remount key.
	//
	// No-ops while a background create for a NEWER selection is already in flight
	// (`signature !== committedSignature`): the mounted (errored) provider is for a signature the
	// buyer has already moved past, and `evict()` would null the create hook's cache while that
	// OTHER create is still pending — the remount's own create then falls through to the "queue
	// behind the pending one" path instead of the cache-reuse path, firing a genuine duplicate
	// POST once the pending one settles. Once that in-flight create resolves it commits and swaps
	// the provider in anyway, so there is nothing useful for this retry to do in the meantime.
	const onRetry = useCallback(() => {
		if (signature !== committedSignature) {
			return;
		}
		evict();
		setSeedSessionPromise(null);
		setRetryAttempt(attempt => attempt + 1);
	}, [evict, signature, committedSignature]);

	// Navigation lives in the component (not the create hook). Fire after render so we don't push
	// during render; effect deps keep it to a single navigation for the fresh-arrival edge.
	useEffect(() => {
		if (arrivedSubscribed) {
			routerPush('/app');
		}
	}, [arrivedSubscribed, routerPush]);

	if (arrivedSubscribed) {
		// Don't mount the provider for an already-subscribed arrival — that would create a session
		// (rejected with `already_subscribed`) and otherwise strand the buyer on a Loader. The
		// effect above navigates to /app.
		return null;
	}

	// True while the live selection hasn't been committed yet, a background create is still
	// resolving for what's already committed (`isCreating`, e.g. a revert whose own detour gets
	// QUEUED, not cancelled), OR the most recent such attempt FAILED. That third condition matters
	// on its own: a revert-to-committed whose queued create fails (e.g. offline) settles with
	// `signature` already back to `committedSignature` and `isCreating` drained to `false` —
	// without it, neither of the other two conditions holds, hiding the overlay (and its error +
	// retry) entirely and leaving a fully enabled submit button over stale data with no error in
	// sight. `backgroundCreateStatus` always resets to `'pending'` at the start of the next real
	// attempt, and a superseded run's sequence guard keeps it from ever writing a stale value, so
	// a lingering `'failed'` here can only mean the current selection's latest attempt failed.
	// Excludes the post-purchase `hasCompletedPurchaseThisMount` case for the same reason the
	// pre-fetch effect above does — that divergence never commits, so the overlay would just get
	// stuck. Deliberately NOT excluded for a buyer who merely toggled away from a family purchase
	// while already owning an unrelated subscription (see that flag's own comment) — for them this
	// stays visible, same as any other genuine background create.
	const isUpdating =
		(signature !== committedSignature || isCreating || backgroundCreateStatus === 'failed') &&
		!hasCompletedPurchaseThisMount;

	return (
		<CheckoutSessionsProvider
			// `sessionGeneration` folds in a remount for the rare case where a resolved session is
			// genuinely different from the mounted one despite `committedSignature`/`retryAttempt`
			// both being unchanged (see `sessionGeneration`'s own comment above).
			key={`${committedSignature}-${retryAttempt}-${sessionGeneration}`}
			createCheckoutSession={createCheckoutSession}
			onRetry={onRetry}
			isCreating={isCreating}
			purchaseArgs={purchaseArgs}
			signatureRef={signatureRef}
			committedSignatureRef={committedSignatureRef}
			// Consumed only once, as this mount's LAZY initial state — harmless to pass on every
			// render since it's always kept in lockstep with `committedSignature` (set together,
			// same effect callback) and cleared on "Try again", so whichever mount actually reads
			// it is guaranteed the resolved session for exactly the signature it's keyed with.
			initialSessionPromise={seedSessionPromise}
			isUpdating={isUpdating}
			backgroundCreateStatus={backgroundCreateStatus}
			onRetryUpdate={onRetryUpdate}
			onSessionMounted={onSessionMounted}
			onInitialCreateFailed={onInitialCreateFailed}
			{...props}
		/>
	);
};

export default CheckoutSessionsForm;
